import { reactive } from 'vue';
import mitt from 'mitt';
import NProgress from 'nprogress';
import { io } from 'socket.io-client';
import { sdkApi as SDK } from 'michollo-sdk';
import Swal from 'sweetalert2';
import XLSX from 'xlsx';
import DOMPurify from 'dompurify';
import { Modal } from 'ant-design-vue';

// CONFIG
import { registerStyles } from './_styles';
import './_icons';
import moment from './_moment';
import router from './_router';
import store, { injectServices } from '../store';
import vars from './_variables';
import validate from './_validation';

// AXIOS
import AxiosHttp from './_axios/_http';
import AxiosApi from './_axios/_api';

// SDK & APP
import appComponent from '../app';
import helpers from '../helpers';

// FILTERS & GLOBAL COMPONENTS
import { registerComponents } from './_components';
import { registerFilters } from './_filters';

// NPROGRESS
const nprogress = NProgress.configure({ parent: 'body' });

// EVENT BUS (replaces new Vue())
const hub = mitt();

// SOCKET.IO (direct, no vue-socket.io wrapper)
const socket = io('https://radar.michollo.com', { autoConnect: true });

// Global reactive state (replaces vue-page-title + postUrl)
const globalState = reactive({ postUrl: '', title: '' });

function configureApp(app) {
	// Compat mode
	app.config.compilerOptions.compatConfig = {
		MODE: 2,
	};

	// Register plugins
	app.use(router);
	app.use(store);

	// Register styles (Ant Design Vue 3)
	registerStyles(app);

	// Register global components
	registerComponents(app);

	// Register filters as global methods
	registerFilters(app);

	// Prototypes → globalProperties
	app.config.globalProperties.$axios = new AxiosHttp().axios;
	app.config.globalProperties.$http = new AxiosApi(nprogress).axios;
	app.config.globalProperties.$hub = hub;
	app.config.globalProperties.$sdk = SDK;
	app.config.globalProperties.$validate = validate;
	app.config.globalProperties.$swal = Swal;
	app.config.globalProperties.$XLSX = XLSX;
	app.config.globalProperties.$moment = moment;
	app.config.globalProperties.$sanitize = html => DOMPurify.sanitize(html, {
		ALLOWED_TAGS: ['a'],
		ALLOWED_ATTR: ['href', 'target'],
	});

	// Socket
	app.config.globalProperties.$socket = socket;
	app.config.globalProperties.$modal = Modal;

	// Socket → Vuex integration
	socket.onAny((event, ...args) => {
		const actionName = `socket${event}`;
		const mutationName = `socket${event}`;

		if (store._actions[actionName]) {
			store.dispatch(actionName, ...args);
		}
		if (store._mutations[mutationName]) {
			store.commit(mutationName, ...args);
		}
	});

	// Global mixin for helpers (maintains $root.xxx() pattern)
	app.mixin(helpers);

	// Global mixin for $postUrl and $title (replaces vue-page-title)
	app.mixin({
		computed: {
			$postUrl: {
				get() { return globalState.postUrl; },
				set(newPostUrl) { globalState.postUrl = newPostUrl; },
			},
			$title: {
				get() { return globalState.title; },
				set(newTitle) {
					globalState.title = newTitle;
					document.title = newTitle ? `MiChollo | ${newTitle}` : 'MiChollo';
				},
			},
		},
	});

	app.config.globalProperties.$vars = vars;
	app.config.globalProperties.$colors = vars.colors;

	// Inject services into Vuex store (replaces this._vm.$xxx in actions)
	injectServices(store, {
		$sdk: SDK,
		$http: app.config.globalProperties.$http,
		$axios: app.config.globalProperties.$axios,
		$moment: moment,
		$socket: socket,
		$hub: hub,
		$message: app.config.globalProperties.$message,
		$notification: app.config.globalProperties.$notification,
	});
}

export {
	router, store, vars, appComponent as app, nprogress, helpers, configureApp,
};
